import apiClient from '@/lib/api-client';
import type {
  TeamMember,
  TeamProvider,
  TeamProviderService,
  TeamListResponse,
  TeamListParams,
} from '@/types/team';

interface BackendService {
  id: number;
  name: string;
  duration_minutes?: number;
}

interface BackendProvider {
  id: number;
  public_id?: string;
  title?: string;
  specialization?: string;
  bio?: string;
  active: boolean;
  services: BackendService[];
  booking_counts: { upcoming: number; past: number };
}

interface BackendTeamMember {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone?: string;
  role: 'admin' | 'staff';
  active: boolean;
  last_sign_in_at?: string;
  created_at: string;
  provider: BackendProvider | null;
}

interface BackendMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

function transformService(s: BackendService): TeamProviderService {
  return { id: s.id, name: s.name, durationMinutes: s.duration_minutes };
}

function transformProvider(p: BackendProvider): TeamProvider {
  return {
    id: p.id,
    publicId: p.public_id,
    title: p.title,
    specialization: p.specialization,
    bio: p.bio,
    active: p.active,
    services: (p.services ?? []).map(transformService),
    bookingCounts: {
      upcoming: p.booking_counts?.upcoming ?? 0,
      past: p.booking_counts?.past ?? 0,
    },
  };
}

function transformMember(m: BackendTeamMember): TeamMember {
  return {
    id: m.id,
    firstName: m.first_name,
    lastName: m.last_name,
    fullName: m.full_name,
    email: m.email,
    phone: m.phone,
    role: m.role,
    active: m.active,
    lastSignInAt: m.last_sign_in_at,
    createdAt: m.created_at,
    provider: m.provider ? transformProvider(m.provider) : null,
  };
}

function transformMeta(m: BackendMeta): TeamListResponse['meta'] {
  return {
    currentPage: m.current_page,
    totalPages: m.total_pages,
    totalCount: m.total_count,
    perPage: m.per_page,
  };
}

class TeamService {
  async list(params: TeamListParams = {}): Promise<TeamListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? 1,
      per_page: params.perPage ?? 20,
    };
    if (params.search?.trim()) queryParams.search = params.search.trim();
    if (params.role && params.role !== 'all') queryParams.role = params.role;
    if (params.status && params.status !== 'all') queryParams.status = params.status;

    const { data } = await apiClient.get<{
      data: { members: BackendTeamMember[]; meta: BackendMeta };
    }>('/v1/admin/team', { params: queryParams });

    return {
      members: data.data.members.map(transformMember),
      meta: transformMeta(data.data.meta),
    };
  }

  async getMember(id: number | string): Promise<TeamMember> {
    const { data } = await apiClient.get<{
      data: { member: BackendTeamMember };
    }>(`/v1/admin/team/${id}`);
    return transformMember(data.data.member);
  }
}

export const teamService = new TeamService();
